import React, { FC } from 'react';
import { BodyTextLarge, FlexCol, FlexRow, H3Text, LinkAsButton } from '@minecraft.atoms';
import { useTranslation } from '@blocs.i18n';
import { UseBilling } from '../useBilling';
import { useBillingPopup } from '../BillingPopupContext';
import { shouldShowBillingStateField } from './PaymentForm/shouldShowBillingStateField';

export type BillingAddressCardProps = {
  selectedPaymentOption?: UseBilling['wallet'][0];
  onEdit: () => void;
};

export const getBillingAddressLines = (
  billingInfo?: UseBilling['wallet'][0],
  getCountryName?: (code: string) => string | undefined
): string[] => {
  if (!billingInfo) return [];

  const cityRegionPostal = [[billingInfo.city, billingInfo.region].filter(Boolean).join(', '), billingInfo.postalCode]
    .filter(Boolean)
    .join(' ');
  const countryName = (billingInfo.country && getCountryName?.(billingInfo.country)) || billingInfo.country;

  return [billingInfo.street1, billingInfo.street2, cityRegionPostal, countryName].filter(Boolean);
};

// The 5 fields AC1 marks Required (address_line_2 is the only optional one). Anything less means
// there's nothing valid to charge tax against — see WalletPicker's WalletBillingAddressEditor, which
// forces editing (no Cancel out) rather than showing this read-only view until the address is complete.
// region is only required for the countries shouldShowBillingStateField actually shows the field for
// (US/CA/IT/NL) — everywhere else it's never collected, so requiring it unconditionally here would
// treat a genuinely complete non-US/CA/IT/NL address as incomplete and force it into edit mode.
export const hasCompleteBillingAddress = (billingInfo?: UseBilling['wallet'][0]): boolean =>
  !!billingInfo?.street1 &&
  !!billingInfo?.city &&
  (!shouldShowBillingStateField(billingInfo?.country) || !!billingInfo?.region) &&
  !!billingInfo?.postalCode &&
  !!billingInfo?.country;

export const BillingAddressCard: FC<BillingAddressCardProps> = ({ selectedPaymentOption, onEdit }) => {
  const { t } = useTranslation();
  const { getCountryName } = useBillingPopup();
  const addressLines = getBillingAddressLines(selectedPaymentOption, getCountryName);

  // WalletBillingAddressEditor only renders this view once the address is complete — an incomplete
  // one is routed straight to the forced edit form instead, so there's nothing meaningful to show here.
  if (!selectedPaymentOption || !addressLines.length) return null;

  return (
    <FlexCol w="full" gap="2" data-testid="billing-address-card">
      <FlexRow w="full" justifyContent="space-between" alignItems="center" wrap="nowrap">
        <H3Text>{t('common:paywall.checkout.billingAddress.title')}</H3Text>
        <LinkAsButton
          type="button"
          data-testid="edit-billing-address-button"
          onClick={onEdit}
          label={t('common:button.edit')}
        />
      </FlexRow>
      <FlexCol w="full">
        {addressLines.map((line) => (
          <BodyTextLarge key={line}>{line}</BodyTextLarge>
        ))}
      </FlexCol>
    </FlexCol>
  );
};
